<script lang="ts" setup>
import { CriteriaInstance } from "types/Checklist";
import { useDocumentHighlightStore } from "~/composables/useDocumentHighlightStore";
import {
  EvaluationCriteria,
  EvaluationRelevantInstance,
  EvaluationResults,
} from "~/types/EvaluationResults";

const props = defineProps({
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  evaluationResults: {
    type: Object as PropType<EvaluationResults>,
    required: true,
  },
  checklistCriteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: true,
  },
});

defineEmits(["reportProblem"]);

const documentId = inject("documentId") as any;

const { addPositions } = useDocumentHighlightStore(documentId);
const { tokenPositionsToHighlight } = storeToRefs(
  useDocumentHighlightStore(documentId)
);

const documentTokenIdPositionMap =
  useDocumentTokenIdPositionMapStore(documentId);
const { tokenGlobalPositionFromId } = storeToRefs(documentTokenIdPositionMap);

const addIdsRecursive = (
  relevantInstance: EvaluationRelevantInstance,
  highlightInstances: boolean
) => {
  if (relevantInstance.type == "paragraph") {
    if (relevantInstance.criteria && relevantInstance.criteria.length > 0) {
      for (var criteria of relevantInstance.criteria) {
        for (var relevantInstance of criteria.relevantInstances) {
          addIdsRecursive(relevantInstance, criteria.config.highlightInstances);
        }
      }
    }
  }

  if (highlightInstances == true) {
    var positions = relevantInstance.tokens.map((token) =>
      tokenGlobalPositionFromId.value(token.id)
    );

    var filteredPositions = positions.filter(
      (position) => position != null
    ) as number[];

    addPositions(filteredPositions);
  }
};

if (props.evaluationResults.evaluationResult) {
  for (var evaluationResult of props.evaluationResults.evaluationResult) {
    for (var relevantInstance of evaluationResult.relevantInstances) {
      addIdsRecursive(
        relevantInstance,
        evaluationResult.config.highlightInstances
      );
    }
  }
}

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { mechanicsCriteria, mechanicsAnnotations } = storeToRefs(
  documentChecklistStore
);

const isMechanicsCriteria = computed(() => {
  var checklistCriteriaId = props.criteriaInstance.checklistCriteriaId;

  if (!checklistCriteriaId) {
    return false;
  }

  var criteria = documentChecklistStore.getCriteriaFromId(checklistCriteriaId);

  return criteria?.isMechanicsCriteria == true;
});

const isMechanicsEmptyRecursive = computed(() => {
  if (isMechanicsCriteria.value != true) return true;

  return (
    mechanicsCriteria.value.length == 0 &&
    mechanicsAnnotations.value.length == 0
  );
});

const isEmpty = computed(() => {
  return (
    !props.evaluationResults.evaluationResult && isMechanicsEmptyRecursive.value
  );
});
</script>

<template>
  <div class="border rounded p-4">
    <div
      v-if="isEmpty == true"
      class="italic w-full flex flex-col h-full items-center justify-center text-center"
    >
      <span> None found </span>
    </div>
    <EvaluationResultsCriteriaList
      v-if="criteriaInstance && criteriaInstance?.checklistCriteriaId"
      :criteria-instance="criteriaInstance"
      :criteria-list="evaluationResults.evaluationResult"
      :checklist-criteria="checklistCriteria"
      :is-top-level="true"
      @reportProblem="$emit('reportProblem', $event)"
    />
    <EvaluationResultsMechanicsErrors
      v-if="isMechanicsCriteria"
      :criteria-instance="criteriaInstance"
    />
  </div>
</template>

<style scoped></style>
